import { useEffect, useState } from 'react';

import {
  Accordion,
  AccordionDescription,
  AccordionHeader,
} from '@asphalt-react/accordion';
import { Button } from '@asphalt-react/button';
import { Spinner } from '@asphalt-react/loader';

const LOCALSTORAGE_BULK_CONFIG_KEY = 'BULK_CONFIG';

type Result = {
  method: string;
  className: string;
  methodName: string;
  params: Record<string, unknown>;
  error?: Record<string, unknown>;
  response?: Record<string, unknown>;
};

const WindvaneTester = () => {
  const [inputValue, setInputValue] = useState('');
  const [parsedData, setParsedData] = useState<object[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [params, setParams] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');

  const [isRunningAll, setIsRunningAll] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [successResults, setSuccessResults] = useState<Result[]>([]);
  const [failureResults, setFailureResults] = useState<Result[]>([]);

  useEffect(() => {
    if (localStorage) {
      try {
        const value = localStorage.getItem(LOCALSTORAGE_BULK_CONFIG_KEY);
        if (value) {
          setInputValue(value);
        }
      } catch (err) {
        setOutput((err as object).toString());
      }
    }
  }, []);

  const clearInput = () => {
    setInputValue('');
    setRunning(false);
    setOutput('');
    setIsRunningAll(false);
  };

  const parseInput = () => {
    try {
      const json = JSON.parse(inputValue);
      if (!Array.isArray(json)) {
        throw new Error('Expected JSON array format like output.json');
      }
      setParsedData(json);
      setCurrentIndex(-1);
      setCompletedCount(0);
      setSuccessResults([]);
      setFailureResults([]);
      setIsRunningAll(false);
    } catch (err) {
      alert(err?.toString());
    }
  };

  const runCurrent = (index: number) => {
    if (!parsedData[index]) return;

    const entry = parsedData[index];
    const [fullKey, value] = Object.entries(entry)[0];
    const [className, methodName] = fullKey.split('.');
    const callParams = value.params;

    setParams(JSON.stringify(callParams));
    setCurrentIndex(index);
    setRunning(true);

    if (window.gpContainer?.call) {
      window.gpContainer.call(
        className,
        methodName,
        callParams,
        (res) => {
          setOutput(JSON.stringify(res));
          setRunning(false);
        },
        (err) => {
          setOutput(JSON.stringify(err));
          setRunning(false);
        }
      );
    } else {
      setOutput('Error: No gp container');
      setRunning(false);
    }
  };

  const rerun = () => {
    if (currentIndex < 0) return;
    const entry = parsedData[currentIndex];
    const [fullKey] = Object.entries(entry)[0];
    const [className, methodName] = fullKey.split('.');

    if (window.gpContainer?.call) {
      try {
        window.gpContainer.call(
          className,
          methodName,
          JSON.parse(params),
          (res) => {
            setOutput(JSON.stringify(res));
          },
          (err) => {
            setOutput(JSON.stringify(err));
          }
        );
      } catch (err) {
        setOutput((err as object).toString());
      }
    }
  };

  // Promise wrapper around WindVane.call for sequential async handling
  const callWindVanePromise = (
    className: string,
    methodName: string,
    callParams: Record<string, unknown>
  ) =>
    new Promise((resolve, reject) => {
      if (window.gpContainer?.call) {
        try {
          window.gpContainer.call(
            className,
            methodName,
            callParams,
            (res) => resolve(res),
            (err) => reject(err)
          );
        } catch (e) {
          reject(e);
        }
      } else {
        reject(new Error('window.gpContainer.call is not available'));
      }
    });

  const handleAll = async () => {
    if (!parsedData || parsedData.length === 0) {
      return;
    }

    setIsRunningAll(true);
    setCompletedCount(0);
    setSuccessResults([]);
    setFailureResults([]);

    for (let i = 0; i < parsedData.length; i++) {
      setCurrentIndex(i);
      const entry = parsedData[i];
      const [fullKey, value] = Object.entries(entry)[0];
      const [className, methodName] = fullKey.split('.');
      const callParams = value.params as Record<string, unknown>;
      setParams(JSON.stringify(callParams));

      try {
        const res = (await callWindVanePromise(
          className,
          methodName,
          callParams
        )) as Record<string, unknown>;
        setSuccessResults((prev) => [
          ...prev,
          {
            method: fullKey,
            className,
            methodName,
            params: callParams,
            response: res,
          },
        ]);
      } catch (err) {
        let errStr;
        try {
          errStr = JSON.stringify(err);
        } catch {
          errStr = String(err);
        }
        setOutput(errStr);
        setFailureResults((prev) => [
          ...prev,
          {
            method: fullKey,
            className,
            methodName,
            params: callParams as Record<string, unknown>,
            error: err as Record<string, unknown>,
          },
        ]);
      } finally {
        setCompletedCount((prev) => prev + 1);
      }
    }

    setIsRunningAll(false);
  };

  return (
    <div className="flex gap-2 flex-col w-full">
      <div className="flex justify-between items-center flex-1 w-full">
        <h1 className="text-2xl font-bold">Bulk Tester</h1>
        <Button
          data-testid="bulk-run-all-button"
          onClick={handleAll}
          size="xs"
          disabled={parsedData.length === 0 || isRunningAll}
        >
          Run all
        </Button>
      </div>

      <textarea
        data-testid="bulk-input-textarea"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (localStorage) {
            try {
              localStorage.setItem(
                LOCALSTORAGE_BULK_CONFIG_KEY,
                e.target.value
              );
            } catch (err) {
              setOutput((err as object).toString());
            }
          }
        }}
        rows={5}
        placeholder='Paste JSON array here, e.g. [{"Class.Method": {"params": {}}}]'
        className="border border-black p-2 rounded-lg"
      />
      <div className="flex gap-2">
        <Button
          data-testid="bulk-parse-button"
          onClick={parseInput}
          disabled={isRunningAll}
          className="block"
          size="xs"
        >
          Parse JSON
        </Button>
        <Button
          data-testid="bulk-clear-button"
          onClick={clearInput}
          danger
          disabled={isRunningAll}
          className="block"
          size="xs"
        >
          Clear
        </Button>
      </div>

      {/* Status text shown while running all */}
      {isRunningAll && parsedData.length > 0 && (
        <p data-testid="bulk-progress-status">
          Finished calling {completedCount}/{parsedData.length} methods
        </p>
      )}

      {parsedData.length > 0 &&
        completedCount === parsedData.length &&
        completedCount > 0 && (
          <div className="w-full" data-testid="bulk-results-container">
            <div>
              {successResults.length > 0 && (
                <Accordion>
                  <AccordionHeader>
                    <p
                      data-testid="bulk-success-count"
                      className="text-green-800"
                    >
                      Success: {successResults.length}/{parsedData.length}{' '}
                      methods
                    </p>
                  </AccordionHeader>
                  <AccordionDescription>
                    <div data-testid="bulk-success-results">
                      {successResults.map((result, index) => (
                        <Accordion key={index}>
                          <AccordionHeader>{result.method}</AccordionHeader>
                          <AccordionDescription>
                            <div>
                              <p className="font-bold">Response:</p>
                              <ResponseContainer
                                text={JSON.stringify(result.response)}
                              />
                            </div>
                          </AccordionDescription>
                        </Accordion>
                      ))}
                    </div>
                  </AccordionDescription>
                </Accordion>
              )}

              {/* Spacer */}
              <div className="h-4"></div>

              {failureResults.length > 0 && (
                <Accordion>
                  <AccordionHeader>
                    <p
                      data-testid="bulk-failure-count"
                      className="text-red-800"
                    >
                      Failure: {failureResults.length}/{parsedData.length}{' '}
                      methods
                    </p>
                  </AccordionHeader>
                  <AccordionDescription>
                    <div
                      data-testid="bulk-failure-results"
                      className="flex-col flex gap-3"
                    >
                      {failureResults.map((result, index) => (
                        <Accordion key={index}>
                          <AccordionHeader>{result.method}</AccordionHeader>
                          <AccordionDescription>
                            <div>
                              <p>Error:</p>
                              <ResponseContainer
                                text={JSON.stringify(result.error)}
                              />
                            </div>
                          </AccordionDescription>
                        </Accordion>
                      ))}
                    </div>
                  </AccordionDescription>
                </Accordion>
              )}
            </div>
          </div>
        )}

      <div className="flex justify-end gap-2">
        {!isRunningAll ? (
          <>
            {/* <Button
              onClick={() => runCurrent(currentIndex - 1)}
              disabled={currentIndex <= 0}
            >
              Previous
            </Button> */}

            {currentIndex === -1 ? (
              <Button
                data-testid="bulk-run-button"
                onClick={() => runCurrent(0)}
                disabled={parsedData.length === 0}
                size="xs"
              >
                {running && <Spinner />}
                {!running && 'Run'}
              </Button>
            ) : (
              <>
                <Button
                  data-testid="bulk-prev-button"
                  onClick={() => runCurrent(currentIndex - 1)}
                  disabled={currentIndex <= 0}
                  size="xs"
                >
                  Prev
                </Button>

                <Button
                  data-testid="bulk-next-button"
                  onClick={() => runCurrent(currentIndex + 1)}
                  disabled={currentIndex >= parsedData.length - 1}
                  size="xs"
                >
                  Next
                </Button>
              </>
            )}

            <Button
              data-testid="bulk-rerun-button"
              onClick={rerun}
              disabled={currentIndex === -1}
              size="xs"
            >
              Rerun
            </Button>
          </>
        ) : (
          <Button primary>Running all</Button>
        )}
      </div>

      {currentIndex >= 0 && parsedData[currentIndex] && (
        <div className="mt-4 w-full" data-testid="bulk-current-test-container">
          <p className="font-bold">Index:</p>{' '}
          <span data-testid="bulk-current-index">
            {currentIndex + 1} / {parsedData.length}
          </span>
          <br />
          <p className="font-bold">Method:</p>{' '}
          <span data-testid="bulk-current-method">
            {Object.keys(parsedData[currentIndex])[0]}
          </span>
          <br />
          <p className="font-bold">Params:</p>
          <textarea
            data-testid="bulk-params-textarea"
            value={params}
            onChange={(e) => {
              setParams(e.target.value);
            }}
            className="w-full rounded-lg p-2 border border-slate-600"
          />
          <div className="flex p-4 w-full mt-4 rounded-lg bg-slate-200">
            <p data-testid="bulk-output" className="break-words w-full">
              {output}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const ResponseContainer = ({ text }: { text: string }) => {
  return (
    <div className="p-2 rounded-lg border-slate-600 border bg-slate-50 w-full break-words">
      {text}
    </div>
  );
};

export default WindvaneTester;
